import ActionButton from '@atoms/button';
import { GlassModal } from '@atoms/glassModal';
import BlockBase from '@components/dnd/base/blockBase';
import { Column } from '@components/dnd/base/blockBase/body.block.style';
import { MonacoEditor } from '@components/monaco/monacoEditor.code';
import { useAtom } from '@hooks/useAtom';
import useProcess from '@hooks/useProcess';
import useStore from '@hooks/useStore';
import EndControls from '@library/condition//endControls';
import type { OnChange } from '@monaco-editor/react';
import { FormControl, FormLabel } from '@mui/joy';
import type { DNDBlock } from '@stratumn/studio-shared';
import { type LucideIcon, Variable, WandSparkles } from 'lucide-react';
import { observer } from 'mobx-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { ParamsList } from '@/routes/routes.types';

import { initialCodeConditionData } from './codeCondition.data';

export const CodeConditionIcon: LucideIcon = Variable;

const CodeCondition = (block: DNDBlock) => {
  const [isMonacoModalOpen, setIsMonacoModalOpen] = useState(false);
  const { codeStore } = useStore();
  const process = useProcess();
  const transitionId = useParams()[ParamsList.TransitionId] as string;

  const atom = useAtom({
    type: 'Code Condition',
    initialData: initialCodeConditionData,
    sourceId: block.id,
    parentId: transitionId,
    parentKind: 'transition'
  });

  if (!process) {
    return null;
  }

  if (!(atom && transitionId)) {
    return null;
  }

  const onCodeChange: OnChange = value => {
    if (!value) {
      return;
    }
    atom.data.code = value;
  };

  return (
    <BlockBase
      dndBlock={block}
      endControls={<EndControls dndBlock={block} />}
      hasTitle={false}
      icon={CodeConditionIcon}
    >
      <Column $width="200px">
        <FormControl required size="sm">
          <FormLabel>Condition code</FormLabel>
          <ActionButton
            color="neutral"
            onClick={() => setIsMonacoModalOpen(true)}
            startDecorator={<WandSparkles size={18} />}
            value="Edit condition's code"
            variant="soft"
          />
        </FormControl>
      </Column>
      <GlassModal
        className="h-full w-full px-4"
        header={{
          title: 'Code condition editor',
          description: 'Customize the code of your condition'
        }}
        onClose={() => setIsMonacoModalOpen(false)}
        open={isMonacoModalOpen}
      >
        <section className="-ml-4 h-full w-full overflow-hidden">
          <MonacoEditor
            code={atom.data.code}
            modelId={atom.id}
            onCodeChange={onCodeChange}
            preCode={codeStore.computePreCode()}
          />
        </section>
      </GlassModal>
    </BlockBase>
  );
};

export default observer(CodeCondition);
